import type { PathStep, ReadyModule } from '~/pages/backoffice/admin/sessions/components/ModulePathBuilder'
import type { PickerGroup, PickerUser } from '~/pages/backoffice/admin/sessions/components/ParticipantPicker'
import { useMutation } from '@apollo/client/react'
import { SessionProgressionMode, SessionStatus } from '@zeus/index'
import { AlertTriangle, Loader } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '~/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog'
import { Input } from '~/components/ui/input'
import { GQL_CREATE_SESSION, GQL_UPDATE_SESSION } from '~/gql/sessions/sessions'
import { useBranding } from '~/helpers/branding/branding'
import { toDayInput } from '~/helpers/dates/session-dates'
import ModulePathBuilder from '~/pages/backoffice/admin/sessions/components/ModulePathBuilder'
import ParticipantPicker from '~/pages/backoffice/admin/sessions/components/ParticipantPicker'

export type EditableSession = {
  id: string
  name: string
  status: string
  startDate?: unknown
  endDate?: unknown
  progressionMode?: string | null
  modules?: { unlockAt?: unknown, module?: { id: string } | null }[] | null
  enrolledUsers?: { id: string }[]
}

type IProps = {
  open: boolean
  onCancel: () => void
  onSuccess: () => void | Promise<unknown>
  modules: ReadyModule[]
  users: PickerUser[]
  /** Groupes proposés en raccourci d'inscription (#135). */
  groups: PickerGroup[]
  /** Parcours à éditer ; absent = création. */
  session?: EditableSession
}

/**
 * Le formulaire n'envoie plus d'instant, il envoie le JOUR saisi (`AAAA-MM-JJ`).
 *
 * C'est l'API qui le résout dans le fuseau de l'organisation (#194). Avant, la
 * conversion se faisait ici, par un `new Date('2026-09-01')` qui vaut minuit
 * **UTC** — soit 02:00 à Paris : la question du fuseau se trouvait tranchée par
 * accident dans le navigateur, alors qu'elle appartient à l'organisation.
 *
 * Une chaîne vide devient `null` et non `undefined` : sur une mise à jour, les
 * deux ne disent pas la même chose — `null` retire la borne, `undefined` la
 * laisse telle quelle.
 */
function toDayOrNull(value: string): string | null {
  return value || null
}

function SessionModal({ open, onCancel, onSuccess, modules, users, groups, session }: IProps) {
  const { t } = useTranslation()
  const { timezone } = useBranding()
  const isEdit = !!session

  const [name, setName] = useState(session?.name ?? '')
  const [startDate, setStartDate] = useState(() => toDayInput(session?.startDate, timezone))
  const [endDate, setEndDate] = useState(() => toDayInput(session?.endDate, timezone))
  const [progressionMode, setProgressionMode] = useState<SessionProgressionMode>(
    (session?.progressionMode as SessionProgressionMode) ?? SessionProgressionMode.FREE,
  )
  const [status, setStatus] = useState<SessionStatus>(
    (session?.status as SessionStatus) ?? SessionStatus.ACTIVE,
  )
  const [steps, setSteps] = useState<PathStep[]>(
    () => (session?.modules ?? [])
      .filter(sm => sm.module?.id)
      .map(sm => ({ moduleId: sm.module!.id, unlockAt: toDayInput(sm.unlockAt, timezone) })),
  )
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(session?.enrolledUsers?.map(u => u.id) ?? []),
  )

  const [createSession, { loading: creating }] = useMutation(GQL_CREATE_SESSION)
  const [updateSession, { loading: updating }] = useMutation(GQL_UPDATE_SESSION)
  const loading = creating || updating

  // Modules retirés du parcours en cours d'édition. Leur progression n'est PAS
  // effacée (elle vit dans ScormTracking, sans clé étrangère vers la jointure),
  // mais elle cesse de compter dans l'avancement — et un apprenant qui avait
  // terminé le parcours y repasse « en cours ». Ça se dit avant de valider.
  const initialModuleIds = (session?.modules ?? []).map(sm => sm.module?.id).filter(Boolean)
  const removed = initialModuleIds.filter(id => !steps.some(s => s.moduleId === id))
  const added = steps.filter(s => !initialModuleIds.includes(s.moduleId))
  const enrolledCount = selected.size
  const warnsAboutProgress = isEdit && enrolledCount > 0 && (removed.length > 0 || added.length > 0)

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.error(t('sessions.nameRequired'))
      return
    }
    if (steps.length === 0) {
      toast.error(t('sessions.path.required'))
      return
    }

    const pathInput = steps.map(s => ({
      moduleId: s.moduleId,
      unlockAt: toDayOrNull(s.unlockAt),
    }))

    try {
      if (isEdit) {
        await updateSession({
          variables: {
            id: session.id,
            input: {
              name: name.trim(),
              status,
              progressionMode,
              modules: pathInput,
              userIds: [...selected],
              startDate: toDayOrNull(startDate),
              endDate: toDayOrNull(endDate),
            },
          },
        })
        toast.success(t('sessions.updated'))
      } else {
        await createSession({
          variables: {
            input: {
              name: name.trim(),
              progressionMode,
              modules: pathInput,
              userIds: [...selected],
              startDate: toDayOrNull(startDate),
              endDate: toDayOrNull(endDate),
            },
          },
        })
        toast.success(t('sessions.created'))
      }
      await onSuccess()
      onCancel()
    } catch (error) {
      // Le message de l'API porte la raison exacte du refus (date d'ouverture
      // après la fin du parcours, module en double…) : la remplacer par un
      // « échec » générique obligerait l'admin à deviner ce qu'il a mal saisi.
      const message = error instanceof Error && error.message
        ? error.message
        : t(isEdit ? 'sessions.updateFailed' : 'sessions.createFailed')
      toast.error(message)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onCancel}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? t('sessions.update.title') : t('sessions.create.title')}
          </DialogTitle>
        </DialogHeader>

        <div className="flex max-h-[70vh] flex-col gap-4 overflow-y-auto px-1">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium" htmlFor="session-name">{t('common.name')}</label>
              <Input id="session-name" value={name} onChange={e => setName(e.target.value)} placeholder={t('sessions.namePlaceholder')} />
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium" htmlFor="session-progression">
                {t('sessions.progression.label')}
              </label>
              <select
                id="session-progression"
                value={progressionMode}
                onChange={e => setProgressionMode(e.target.value as SessionProgressionMode)}
                className="h-9 rounded-md border border-border bg-transparent px-3 text-sm"
              >
                <option value={SessionProgressionMode.FREE}>{t('sessions.progression.free')}</option>
                <option value={SessionProgressionMode.SEQUENTIAL}>{t('sessions.progression.sequential')}</option>
              </select>
              <p className="text-xs text-muted-foreground">
                {progressionMode === SessionProgressionMode.SEQUENTIAL
                  ? t('sessions.progression.sequential.hint')
                  : t('sessions.progression.free.hint')}
              </p>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium" htmlFor="session-start">{t('sessions.startDate')}</label>
              <Input id="session-start" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium" htmlFor="session-end">{t('sessions.endDate')}</label>
              <Input id="session-end" type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
            </div>

            {isEdit && (
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium" htmlFor="session-status">{t('common.status')}</label>
                <select
                  id="session-status"
                  value={status}
                  onChange={e => setStatus(e.target.value as SessionStatus)}
                  className="h-9 rounded-md border border-border bg-transparent px-3 text-sm"
                >
                  <option value="ACTIVE">{t('status.active')}</option>
                  <option value="ARCHIVED">{t('status.archived')}</option>
                </select>
              </div>
            )}
          </div>

          <ModulePathBuilder
            modules={modules}
            value={steps}
            onChange={setSteps}
            startDate={startDate}
            endDate={endDate}
          />

          {warnsAboutProgress && (
            <p className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-700 dark:text-amber-400">
              <AlertTriangle className="mt-0.5 size-4 shrink-0" />
              <span>
                {t('sessions.path.warn.inProgress', { count: enrolledCount })}
              </span>
            </p>
          )}

          <ParticipantPicker users={users} groups={groups} value={selected} onChange={setSelected} />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>{t('common.cancel')}</Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading && <Loader className="mr-2 size-4 animate-spin" />}
            {isEdit ? t('common.save') : t('common.create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default SessionModal
